import { ProgramState } from '@rhg/utils/programs';
import { useMemo } from 'react';
import { ProgramContextProps } from '../types';
import { useProgramContextWithGuides } from '../with-guides';
import { useProgramContextWithWorkflow } from '../with-workflow';
import { ProgramContextWithGuidesAndWorkflowState } from './types';

export function useProgramContextWithGuidesAndWorkflow(
    props: ProgramContextProps
): ProgramContextWithGuidesAndWorkflowState {
    const withGuides = useProgramContextWithGuides(props);
    const withWorkflow = useProgramContextWithWorkflow(props);

    const currentSession = useMemo(
        () =>
            withGuides.orderedSessions?.find(
                ({ completed, isTrailer }) =>
                    (withWorkflow.programState !== ProgramState.InProgress ||
                        !isTrailer) &&
                    !completed
            ),
        [withGuides.orderedSessions, withWorkflow.programState]
    );

    const progress = useMemo(
        () =>
            withWorkflow.programState === ProgramState.InProgress &&
            withGuides.numSessions
                ? (withGuides.numSessionsCompleted ?? 0) /
                  withGuides.numSessions
                : undefined,
        [
            withGuides.numSessions,
            withGuides.numSessionsCompleted,
            withWorkflow.programState,
        ]
    );

    return useMemo(
        () => ({
            ...withWorkflow,
            ...withGuides,
            programType: withWorkflow.programType,
            isFetched: withWorkflow.isFetched && withGuides.isFetched,
            progress,
            currentSession,
        }),
        [currentSession, progress, withGuides, withWorkflow]
    );
}
